import { Colors, Heading, Text } from '@amelio/ui-library';

import { AnswerResult } from './AnswerResult';
import { CommentSection } from './CommentSection';
import { QuestionTextRenderer } from '../QuestionTextRenderer/QuestionTextRenderer';

import type { QuestionResultContainerProps } from './types';

import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { getLocalizedText } from '@/utils/localization';
import { isNonCommentableQuestionType } from '@/utils/question';

export const QuestionResultContainer = ({
    data,
    className = '',
    dataTestId = 'question-result-container',
    onReplySubmit,
    isSubmittingReply = false,
    areRepliesClosed = false,
    is360 = false,
    reviewee,
}: QuestionResultContainerProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();

    const { question, answeredCount, totalCount, answerData, textAnswers } = data;

    return (
        <div
            className={`w-full ${className}`}
            data-testid={dataTestId}
        >
            <div className={'mt-6'}>
                <Heading
                    level={3}
                    color={Colors.BASE}
                    className={'text-lg font-semibold'}
                >
                    <QuestionTextRenderer
                        text={replaceDynamicTags({
                            text: getLocalizedText(question.label),
                            employee: reviewee,
                        })}
                    />
                </Heading>
            </div>

            <div className={'mt-4'}>
                <Text
                    color={Colors.BASE_600}
                    size={'small'}
                >
                    {t('questionResult.employeesOutOf', {
                        answeredCount,
                        totalCount,
                    })}
                </Text>
            </div>

            <div className={'border-base-100 mt-6 rounded-lg py-4'}>
                <AnswerResult
                    question={question}
                    answerData={answerData}
                    answeredCount={answeredCount}
                    textAnswers={textAnswers}
                    dataTestId={dataTestId}
                    reviewee={reviewee}
                />
            </div>

            {!isNonCommentableQuestionType(question.questionType) && (
                <CommentSection
                    comments={data.comments}
                    onReplySubmit={onReplySubmit}
                    isSubmittingReply={isSubmittingReply}
                    areRepliesClosed={areRepliesClosed}
                    is360={is360}
                    dataTestId={`${dataTestId}-comments`}
                />
            )}
        </div>
    );
};
